import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Grid,
  Typography,
  Breadcrumbs,
  Button,
  Divider,
  Card,
  Stack,
  Link,
} from "@mui/material";
import { NavLink, useNavigate } from "react-router-dom";
import BackupIcon from "@mui/icons-material/Backup";
import SettingsIcon from "@mui/icons-material/Settings";
import { importUserAndSubscription } from "../../../api/user";
import { Box } from "@mui/system";
import { customColor } from "../../../theme/variants";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { blueBorder, sxBreadcrumbsStyle } from "../../../theme/globalStyles";
import { Download } from "@mui/icons-material";
import ScreenSearchDesktopIcon from "@mui/icons-material/ScreenSearchDesktop";
import SaveIcon from "@mui/icons-material/Save";
import ErrorAlert from "../../../components/ui/ErrorAlert";

const ExcelImport = () => {
  // states
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // objects
  const navigate = useNavigate();

  // functions
  const HelperText = ({ title, ...params }) => {
    return (
      <Typography
        variant="h5"
        textAlign={"left"}
        mt={3}
        mb={1}
        sx={{ color: "grey !important", ...params }}
      >
        {title}
      </Typography>
    );
  };

  const Perek = ({ ...params }) => {
    return (
      <Box
        sx={{
          position: "absolute",
          ...params,
          height: 8,
          width: 8,
          borderRadius: "8px",
          backgroundColor: "white",
          boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
        }}
      />
    );
  };

  const onFileChange = (e) => {
    setFile(e.target.files[0]);
    setFileName(e.target.files[0]?.name);
  };

  // upload excel
  const submitUploadExcel = (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    const data = new FormData();
    data.append("excelFile", file);

    // api calling
    importUserAndSubscription(data)
      .then((response) => {
        console.log("Excel Response: ", response);
        setIsSubmitting(false);
        navigate("/user/all");
      })
      .catch((error) => {
        setIsSubmitting(false);
        setErrorMessage(
          error.response.data.message ||
            "Something went wrong, please try again later"
        );
      });
  };

  return (
    <React.Fragment>
      <Helmet title="Upload Excel" />
      <Grid
        justifyContent="space-between"
        alignItems={"end"}
        container
        spacing={10}
      >
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Upload Excel File
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>

            <Link component={NavLink} to="/user/all">
              Users
            </Link>

            <Typography>Excel Upload</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <Button variant="outlined" onClick={() => navigate("/user/all")}>
            Go Back To User
          </Button>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={6} justifyContent="center" textAlign={"center"}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 15 }}>
            <form onSubmit={submitUploadExcel}>
              <Stack direction="column" gap={3}>
                <Stack
                  direction="row"
                  gap={2}
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    backgroundColor: customColor[100],
                    color: "black",
                    borderRadius: "8px",
                    p: 5,
                    mb: 3,
                    boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                    position: "relative",
                    ...blueBorder,
                  }}
                >
                  <Perek top={5} left={5} />
                  <Perek top={5} right={5} />
                  <Perek bottom={5} left={5} />
                  <Perek bottom={5} right={5} />

                  <Typography variant="h4">
                    Submit your excel file from here. You can simply download
                    this sample excel file to get the format.
                  </Typography>
                </Stack>
                <a
                  href="/static/file/nypd-excel-sheet-sample.xlsx"
                  download
                  style={{
                    textDecoration: "none",
                    display: "block",
                    width: "100%",
                  }}
                >
                  <HelperText title="Download this file to get excel format." />
                  <Button
                    variant="outlined"
                    sx={{
                      width: "100%",
                      fontSize: "16px",
                      fontWeight: "bold",
                      transition: "all .3s ease-in-out",
                      ".download-text": {
                        transition: "all .3s ease-in-out",
                      },
                      ".downloadIcon": {
                        transform: "scale(1.3)",
                        transition: "all .3s ease-in-out",
                      },
                      "&:hover": {
                        ".download-text": {
                          transform: "translateX(-5px)",
                        },
                        ".downloadIcon": {
                          transform: "scale(1.35)",
                        },
                      },
                    }}
                  >
                    <span className="download-text">Click to download</span>
                    <Download className="downloadIcon" sx={{ ml: 2 }} />
                  </Button>
                </a>

                <HelperText title="Step: 1" mb={-2} />
                <Box
                  sx={{
                    border: `1px solid ${customColor[200]}`,
                    borderRadius: "50px",
                    cursor: "pointer",
                    transition: "all .3s ease-in-out",
                    ".file-icon": {
                      transform: "scale(1.3)",
                      transition: "all .3s ease-in-out",
                    },
                    ".file-text": {
                      transition: "all .3s ease-in-out",
                    },
                    "&:hover": {
                      ".file-text": {
                        transform: "translateX(-5px)",
                      },
                      ".file-icon": {
                        transform: "scale(1.35)",
                      },
                    },
                    label: {
                      p: 2,
                      display: "block",
                      fontSize: "16px",
                      fontWeight: "bold",
                      cursor: "pointer",
                    },
                    "#upload-file": {
                      opacity: "0",
                      position: "absolute",
                      zIndex: "-1",
                    },
                  }}
                >
                  <label htmlFor="upload-file">
                    {fileName?.length > 0 ? (
                      `Your file is: ${fileName}`
                    ) : (
                      <Stack
                        direction="row"
                        alignItems={"center"}
                        justifyContent="center"
                        gap={3}
                      >
                        <span className="file-text">Browse Your File...</span>
                        <ScreenSearchDesktopIcon className="file-icon" />
                      </Stack>
                    )}
                  </label>
                  <input
                    type="file"
                    id="upload-file"
                    name="excelFile"
                    accept=".xlsx, .csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    onChange={onFileChange}
                    style={{ margin: "0 auto" }}
                  />
                </Box>

                <HelperText title="Step: 2" mb={-2} />
                <Button
                  variant="contained"
                  type="submit"
                  disabled={file ? isSubmitting : true}
                  sx={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    transition: "all .3s ease-in-out",
                    ".upload-btn": {
                      transform: "scale(1.3)",
                      transition: "all .3s ease-in-out",
                    },
                    ".upload-text": {
                      transition: "all .3s ease-in-out",
                    },
                    "&:hover": {
                      ".upload-text": {
                        transform: "translateX(-5px)",
                      },
                      ".upload-btn": {
                        transform: "scale(1.35)",
                      },
                    },
                  }}
                >
                  <span className="upload-text">Submit Your File</span>
                  <SaveIcon className="upload-btn" sx={{ ml: 3 }} />
                </Button>
                <Box>
                  {errorMessage && <ErrorAlert title={errorMessage} mb={3} />}
                </Box>
              </Stack>
            </form>
          </Card>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default ExcelImport;
